import { useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import {
  checkEmailAccess,
  clearEmailSession,
  getStoredAccess,
  getVerifiedEmail,
  sendEmailCode,
  verifyEmailCode,
} from "../lib/emailGate";
import { errorReporter } from "../lib/errorReporter";

/**
 * The sign-in's email step: the address, then the 6-digit code Supabase mailed.
 * UX, not enforcement: the gateway's 403 at order intake is the security boundary.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type GateState =
  | { kind: "email" }
  | { kind: "sending" }
  | { kind: "code" }
  | { kind: "verifying" }
  | { kind: "waitlisted"; email: string; rechecking: boolean; stillListed: boolean }
  | { kind: "error"; screen: "email" | "code"; message: string };

function explainSendFailure(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    return "Too many codes requested — wait a minute, then try again.";
  }
  return "Could not send the code. Check the address and try again.";
}

function explainVerifyFailure(err: unknown): string {
  if (err instanceof ApiError && (err.status === 403 || err.status === 401)) {
    return "That code is wrong or expired — check it, or request a new one.";
  }
  return "Verification failed. Request a new code and try again.";
}

export function EmailGate({ onVerified }: { onVerified: (email: string) => void }) {
  // A returning waitlisted visitor lands straight on the waitlist screen.
  const [state, setState] = useState<GateState>(() => {
    const stored = getVerifiedEmail();
    const access = getStoredAccess();
    return stored !== null && (access === "waitlisted" || access === "already")
      ? { kind: "waitlisted", email: stored, rechecking: false, stillListed: false }
      : { kind: "email" };
  });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  // The address a code was actually sent to — `email` may be edited afterwards.
  const sentToRef = useRef("");

  // A stored session with no allowlist decision is resolved once, not asked for a code again.
  useEffect(() => {
    if (getVerifiedEmail() === null || getStoredAccess() !== null) return;
    void checkEmailAccess().then((access) => {
      const stored = getVerifiedEmail();
      if (stored === null) return;
      if (access === "approved") onVerified(stored);
      else if (access !== null) {
        setState({ kind: "waitlisted", email: stored, rechecking: false, stillListed: false });
      }
    });
    // Mount-only: the stored session cannot change underneath this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = state.kind === "sending" || state.kind === "verifying";
  const screen: "email" | "code" =
    state.kind === "code" || state.kind === "verifying"
      ? "code"
      : state.kind === "error"
        ? state.screen
        : "email";

  /** Route a fresh allowlist answer: approved opens the wallet step. */
  function routeAccess(target: string, access: string | null) {
    if (access === "waitlisted" || access === "already") {
      setState({ kind: "waitlisted", email: target, rechecking: false, stillListed: false });
      return;
    }
    // null ("cannot say") passes too: the gateway re-checks at login.
    onVerified(target);
  }

  function sendCode() {
    const target = email.trim().toLowerCase();
    if (!EMAIL_RE.test(target)) {
      setState({ kind: "error", screen: "email", message: "Enter a valid email address." });
      return;
    }
    setState({ kind: "sending" });
    sendEmailCode(target).then(
      () => {
        sentToRef.current = target;
        setCode("");
        setState({ kind: "code" });
      },
      (err: unknown) => {
        // A 429 is expected; a gate that cannot send codes otherwise is broken.
        if (!(err instanceof ApiError && err.status === 429)) {
          errorReporter.captureException(err, { source: "email-gate", stage: "send" });
        }
        setState({ kind: "error", screen: "email", message: explainSendFailure(err) });
      },
    );
  }

  function verify() {
    const digits = code.trim();
    if (!/^\d{6}$/.test(digits)) {
      setState({ kind: "error", screen: "code", message: "Enter the 6-digit code." });
      return;
    }
    setState({ kind: "verifying" });
    verifyEmailCode(sentToRef.current, digits).then(
      // Ownership proven — now ask the allowlist whether this email may in.
      (session) => checkEmailAccess().then((access) => routeAccess(session.email, access)),
      (err: unknown) => {
        if (!(err instanceof ApiError && (err.status === 403 || err.status === 401))) {
          errorReporter.captureException(err, { source: "email-gate", stage: "verify" });
        }
        setState({ kind: "error", screen: "code", message: explainVerifyFailure(err) });
      },
    );
  }

  function recheck() {
    if (state.kind !== "waitlisted" || state.rechecking) return;
    setState({ ...state, rechecking: true, stillListed: false });
    void checkEmailAccess().then((access) => {
      if (access === "approved") {
        onVerified(state.email);
        return;
      }
      setState({ kind: "waitlisted", email: state.email, rechecking: false, stillListed: true });
    });
  }

  if (state.kind === "waitlisted") {
    return (
      <div className="email-gate">
        <p className="login-hint">
          <strong>{state.email}</strong> is on the waitlist. We&apos;ll email you the moment your
          access is ready.
        </p>
        <button
          type="button"
          className="btn btn-primary login-submit"
          onClick={recheck}
          disabled={state.rechecking}
        >
          {state.rechecking ? "Checking…" : "Check again"}
        </button>
        {state.stillListed && (
          <div className="inline-banner inline-banner-warn" role="status">
            Still on the list — we&apos;ll let you know.
          </div>
        )}
        <button
          type="button"
          className="link-button login-wallet-switch"
          onClick={() => {
            clearEmailSession();
            setEmail("");
            setCode("");
            setState({ kind: "email" });
          }}
          disabled={state.rechecking}
        >
          Use a different email
        </button>
      </div>
    );
  }

  if (screen === "code") {
    return (
      <div className="email-gate">
        <p className="login-hint">
          We sent a 6-digit code to <strong>{sentToRef.current}</strong>
        </p>
        <label className="login-label" htmlFor="email-gate-code">
          Verification code
        </label>
        <input
          id="email-gate-code"
          className="login-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          value={code}
          disabled={busy}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") verify();
          }}
        />
        <button
          type="button"
          className="btn btn-primary login-submit"
          onClick={verify}
          disabled={busy || code.length !== 6}
        >
          {state.kind === "verifying" ? "Verifying…" : "Verify"}
        </button>
        {state.kind === "error" && (
          <div className="inline-banner inline-banner-error" role="alert">
            {state.message}
          </div>
        )}
        <button
          type="button"
          className="link-button login-wallet-switch"
          onClick={() => {
            setState({ kind: "email" });
            setCode("");
          }}
          disabled={busy}
        >
          Use a different email or resend the code
        </button>
      </div>
    );
  }

  return (
    <div className="email-gate">
      <label className="login-label" htmlFor="email-gate-address">
        Email
      </label>
      <input
        id="email-gate-address"
        className="login-input"
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        disabled={busy}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") sendCode();
        }}
      />
      <button
        type="button"
        className="btn btn-primary login-submit"
        onClick={sendCode}
        disabled={busy}
      >
        {state.kind === "sending" ? "Sending…" : "Send code"}
      </button>
      {state.kind === "error" && (
        <div className="inline-banner inline-banner-error" role="alert">
          {state.message}
        </div>
      )}
    </div>
  );
}
